import type { AutosaveTriggerName, CheckpointMeta, LinkHostMode } from "@redchain/contracts";
import type { SessionHeader } from "@redchain/input-log";

/**
 * Main ↔ worker messages, exactly as in IMPLEMENTATION_PLAN §6.23.
 * `ready`, `stopped` and `error` (worker → main) are lifecycle additions; the `link*` messages
 * and `link` status are the link-play client (WEB-20, §17.2).
 */

export const FAST_FORWARD_MAX = 8 as const;

export type InitMessage = {
  type: "init";
  rom: Uint8Array;
  sram: Uint8Array;
  sessionHeader: SessionHeader;
  autosaveTriggers: AutosaveTriggerName[];
  startFrame: 0n;
};

export type MainToWorker =
  | InitMessage
  | { type: "mask"; mask: number }
  | { type: "fastForward"; on: boolean; maxSpeed: typeof FAST_FORWARD_MAX }
  | { type: "powerCycle" }
  | { type: "pause" }
  | { type: "resume" }
  | { type: "superseded" }
  /** worklet → worker (relayed by the main thread): audio buffered, in emulated frames. */
  | { type: "level"; bufferedFrames: number }
  | LinkArmMessage
  | LinkConnectMessage
  /** Leave the link: before `ready` the paused core resumes solo; afterwards the segment ends as `peer_left`. */
  | { type: "linkCancel" }
  /** Answer to a worker `linkToken` request: a freshly refreshed API access token (empty if signed out). */
  | { type: "linkToken"; token: string }
  /** Server-issued purse credit (SSE `purse.credit`): written into the purse at the next safe point. */
  | { type: "purseCredit"; creditId: string; amount: number }
  /** Clear Start-menu WALLET handshake (wRedChainWalletReq → 0) after the Gen1 Wallet UI closes. */
  | { type: "walletClose" }
  /** Clear the Cable Club board handshake (wRedChainBoardReq → 0) after the board overlay closes. */
  | { type: "boardClose" }
  /** Pause for the in-room invite prompt. Writes no game memory and no input-log record. */
  | { type: "inviteHold" }
  | { type: "inviteRelease" }
  /** After linkArm: append UNION and write wRedChainUnionDest. 1 Trade Center, 2 Colosseum. */
  | { type: "union"; lobbyId: string; dest: 1 | 2 }
  /** Read the live party so a Cable Club post uses the Pokémon in the game. */
  | { type: "readParty" }
  /** Live map position for the Cable Club proof. Reads WRAM only. */
  | { type: "probe" };

/** The next `game` checkpoint (the Cable Club forced save, §17.1) pauses the core for this lobby. */
export type LinkArmMessage = { type: "linkArm"; lobbyId: string; hostMode: LinkHostMode };

/** Sent once the server has verified both Cable Club states: open /link/ws and run the segment. */
export type LinkConnectMessage = {
  type: "linkConnect";
  url: string;
  /** API access token, offered as the `bearer.<jwt>` subprotocol. */
  token: string;
  lobbyId: string;
  playerId: string;
  /** This player's solo session (HELLO). */
  sessionId: string;
  /** Lockstep only: the opponent's ROM when it differs from ours (Red ↔ Blue, §17.2); null = same ROM. */
  opponentRom: Uint8Array | null;
};

export type LinkEndOutcome = "done" | "aborted" | "desync";

/** Worker → main link status (§17.2 / §17.2.1). */
export type LinkStatus =
  | { phase: "armed"; lobbyId: string; hostMode: LinkHostMode }
  /** Core paused right after the Cable Club checkpoint at `checkpointFrame`; waiting for `linkConnect`. */
  | { phase: "paused"; lobbyId: string; checkpointFrame: bigint }
  | { phase: "connecting" }
  /** Socket open, HELLO sent: waiting for the opponent, the verified state and the RTT probes. */
  | { phase: "handshake"; stateLoaded: boolean }
  | { phase: "running"; hostMode: LinkHostMode; side: "A" | "B"; inputDelay: number; linkSessionId: string; stalled: boolean; inLinkRoom?: boolean }
  | { phase: "desync"; frame: number }
  | {
      phase: "ended";
      outcome: LinkEndOutcome;
      reason: string;
      /** Link steps both cores ran (lockstep); 0 in server-run mode, where the local core never steps. */
      steps: number;
      /** Server-run: the local core is still at the Cable Club save, so the player must reload (§17.2.1). */
      needsReload: boolean;
    }
  /** Disarmed before the segment started; solo play continues. */
  | { phase: "cancelled" }
  | { phase: "error"; message: string };

export interface AutosaveNewMon {
  species: number;
  name: string;
}

export interface AutosaveRareFound {
  index: number;
  name: string;
  count: number;
}

export type ChunkMessage = {
  type: "chunk";
  seq: number;
  bytes: Uint8Array;
  /** Lowercase hex of SHA-256(prev ‖ chunk). */
  chainHash: string;
  firstFrame: bigint;
  lastFrame: bigint;
};

export type CheckpointMessage = { type: "checkpoint"; meta: CheckpointMeta; sram: Uint8Array };

export type AutosaveMessage = {
  type: "autosave";
  triggers: AutosaveTriggerName[];
  frame: bigint;
  moneyDelta: number;
  newMons: AutosaveNewMon[];
  rareFound: AutosaveRareFound[];
};

export type WorkerToMain =
  | { type: "frame"; pixels: ArrayBuffer }
  | { type: "audio"; samples: Float32Array }
  | ChunkMessage
  | CheckpointMessage
  | AutosaveMessage
  | { type: "ready"; frame: bigint }
  | { type: "stopped"; frame: bigint }
  | { type: "link"; status: LinkStatus }
  /** A purse credit was written into the game; `applied` is the change after clamping to ₽0…999,999. */
  | { type: "purseApplied"; creditId: string; amount: number; applied: number; purse: number }
  /** Start menu WALLET was selected (wRedChainWalletReq == 1 at an overworld safe point). */
  | { type: "walletOpen" }
  /** Cable Club board request was set at an overworld safe point. Opening appends no record. */
  | { type: "boardOpen"; board: 1 | 2 }
  | { type: "party"; trainer: string; map: number; slots: { species: number; level: number; minted: boolean }[] }
  | {
      type: "probe";
      map: number;
      x: number;
      y: number;
      facing: number;
      joy: number;
      walkCounter: number;
      fontLoaded: number;
      frame: number;
      hasPokedex: boolean;
      hasParcel: boolean;
      startSaveIndex: number;
      overworldPolls: number;
      flags5: number;
      npc: number;
      wy: number;
      party: number;
      battle: number;
      link: number;
      menu: number;
      menuX: number;
      menuKeys: number;
      tradeStage: number;
      tradeMenuSide: number;
      tradeChoices: boolean;
      tradeConfirmChoices: boolean;
      tradeDialogue: boolean;
      statsScreen: boolean;
      script: number;
      serial: number;
      trade: boolean;
      tradeCompleted: boolean;
      tradeCancelMenu: boolean;
      battleMenu: boolean;
      partyIdentity: number[];
    }
  | { type: "error"; message: string }
  /** Server-run reconnect needs a new access token before it opens the socket again. */
  | { type: "linkToken" };

/** Game Boy button mask bits (§8.4). */
export const Button = {
  A: 1 << 0,
  B: 1 << 1,
  SELECT: 1 << 2,
  START: 1 << 3,
  RIGHT: 1 << 4,
  LEFT: 1 << 5,
  UP: 1 << 6,
  DOWN: 1 << 7,
} as const;
export type ButtonName = keyof typeof Button;

export const SCREEN_WIDTH = 160;
export const SCREEN_HEIGHT = 144;
export const AUDIO_SAMPLE_RATE = 48_000;
export const AUDIO_CHANNELS = 2;
/** Audio frames (per channel) produced by one emulated step at 59.7275 Hz. */
export const AUDIO_FRAMES_PER_STEP = AUDIO_SAMPLE_RATE / 59.7275;
